import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getMotorcycleWithParts } from '../api';
import ProductCard from '../components/ProductCard';

export default function MotorcycleDetail() {
  const { id } = useParams();
  const [moto, setMoto] = useState(null);
  const [compatibleParts, setCompatibleParts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getMotorcycleWithParts(id)
      .then(data => {
        setMoto(data.motorcycle);
        setCompatibleParts(data.compatibleParts || []);
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div style={styles.loading}>Loading...</div>;
  if (!moto) return <div style={styles.loading}>Motorcycle not found.</div>;

  const specs = [
    { label: 'Year', value: moto.year },
    { label: 'Make', value: moto.make },
    { label: 'Model', value: moto.model },
    { label: 'Condition', value: moto.condition },
    ...(moto.mileage != null ? [{ label: 'Mileage', value: `${moto.mileage.toLocaleString()} km` }] : []),
    ...(moto.displacement ? [{ label: 'Displacement', value: `${moto.displacement}cc` }] : []),
    ...(moto.engineType ? [{ label: 'Engine Type', value: moto.engineType }] : []),
    ...(moto.colour ? [{ label: 'Colour', value: moto.colour }] : []),
    ...(moto.vin ? [{ label: 'VIN', value: moto.vin }] : [])
  ];

  return (
    <div style={styles.container}>
      {/* Hero */}
      <div style={styles.hero}>
        {moto.images?.[0] ? (
          <img src={moto.images[0]} alt={moto.title} style={styles.heroImage} />
        ) : (
          <div style={styles.heroPlaceholder}>
            <span style={{ fontSize: 80 }}>🏍️</span>
          </div>
        )}
        <div style={styles.heroOverlay}>
          <span style={styles.conditionBadge}>{moto.condition}</span>
          <h1 style={styles.title}>{moto.year} {moto.make} {moto.model}</h1>
          <p style={styles.price}>${moto.price?.toLocaleString('en-CA')}</p>
          <span style={styles.sku}>SKU: {moto.sku}</span>
        </div>
      </div>

      {/* Details */}
      <div style={styles.detailGrid}>
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Specifications</h2>
          <div style={styles.specsGrid}>
            {specs.map(s => (
              <div key={s.label} style={styles.specItem}>
                <span style={styles.specLabel}>{s.label}</span>
                <span style={styles.specValue}>{s.value}</span>
              </div>
            ))}
          </div>
        </div>

        {moto.description && (
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Description</h2>
            <div style={styles.description}>{moto.description}</div>
          </div>
        )}

        {/* Compatible Parts */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Compatible Parts</h2>
          {compatibleParts.length === 0 ? (
            <p style={styles.empty}>
              No compatible parts currently in stock.{' '}
              <Link to="/contact" style={styles.contactLink}>Contact us</Link> to inquire.
            </p>
          ) : (
            <div style={styles.partsGrid}>
              {compatibleParts.slice(0, 8).map(p => (
                <Link key={p._id} to={`/product/${p._id}`}>
                  <ProductCard product={p} />
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: { maxWidth: 1200, margin: '0 auto', padding: 24 },
  loading: { textAlign: 'center', padding: 60, color: '#94a3b8' },
  hero: {
    position: 'relative',
    borderRadius: 16,
    overflow: 'hidden',
    marginBottom: 32,
    background: '#f8fafc'
  },
  heroImage: { width: '100%', height: 400, objectFit: 'cover' },
  heroPlaceholder: {
    width: '100%', height: 400, display: 'flex', alignItems: 'center',
    justifyContent: 'center', background: '#f1f5f9'
  },
  heroOverlay: {
    padding: 24, background: '#fff'
  },
  conditionBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: 6,
    background: '#4f46e5',
    color: '#fff',
    fontSize: 12,
    fontWeight: 600,
    marginBottom: 8
  },
  title: { fontSize: 32, fontWeight: 700, color: '#1e293b', margin: '0 0 8px' },
  price: { fontSize: 28, fontWeight: 700, color: '#4f46e5', margin: '0 0 4px' },
  sku: { fontSize: 13, color: '#94a3b8' },
  detailGrid: { display: 'flex', flexDirection: 'column', gap: 32 },
  section: { background: '#fff', borderRadius: 12, padding: 24, border: '1px solid #e2e8f0' },
  sectionTitle: { fontSize: 20, fontWeight: 600, color: '#1e293b', margin: '0 0 16px' },
  specsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 },
  specItem: { display: 'flex', flexDirection: 'column', gap: 4 },
  specLabel: { fontSize: 12, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase' },
  specValue: { fontSize: 16, color: '#1e293b' },
  description: { fontSize: 15, color: '#475569', lineHeight: 1.7, whiteSpace: 'pre-wrap' },
  empty: { fontSize: 15, color: '#94a3b8', padding: 24, textAlign: 'center' },
  contactLink: { color: '#4f46e5' },
  partsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }
};